import { Input, Modal, PrimaryButton, PrimaryCardButton, RadioButtonGroup, TextArea } from '@cb/apricot-react';
import { Grid } from '@mui/material';
import { ChangeEventHandler, FC, useEffect, useState } from 'react';

import { RadioButton, WordBreak } from 'src/components';
import { authorizationLevelOptions } from 'src/constants';
import { useToasts } from 'src/hooks';
import { ISummary } from 'src/types';
import { statusUpdate } from '../../../services/schoolRequest';
import { useAuth } from 'src/contexts';
import { CircularProgress } from '@mui/material';

interface ApprovedModalProps {
  isOpen?: boolean;
  onClose: VoidFunction;
  onComplete: (request: ISummary) => void;
  request: ISummary | null;
}

const ApprovedModal: FC<ApprovedModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  request,
}) => {
  const { requestType } = request || {};
  const isCreate = requestType === 'update' ? false : true;

  const { userName } = useAuth();
  const [schoolCode, setSchoolCode] = useState('');
  const [loading, setLoading] = useState('');
  const [aiLevel, setAiLevel] = useState('');
  const [statusComments, setStatusComments] = useState('');
  const { errorMessage, render: renderToasts, toastErrorMessage } = useToasts();

  useEffect(() => {
    setAiLevel(request?.aiLevelInd || '');
    setSchoolCode(request?.aiCode || '');
    setStatusComments('');
  }, [request]);

  const handleChangeSchoolCode: ChangeEventHandler<HTMLInputElement> = (event) => {
    setSchoolCode(event.target.value);
  };

  const handleChangeAiLevel = (value: string) => {
    setAiLevel(value);
  };

  const handleChangeStatusComments = (event: any) => {
    setStatusComments(event.target.value);
  };

  const handleSubmit = async () => {
    if (!request || !schoolCode || !aiLevel) {
      toastErrorMessage('Incomplete data -- please fill School Code and/or Select Level');
    } else {
      toastErrorMessage(null);
      setLoading('Saving');

      try {
        const updated = await statusUpdate(request.userId, request.categoryId, {
          user: userName,
          status: 'completed',
          aiCode: schoolCode,
          aiLevelInd: aiLevel,
          comment: statusComments
        });
        request.updateDate = updated.time;
        onComplete(request);
      } catch (error: any) {
        toastErrorMessage(error.message);
      }
      setLoading('');
    }
  };

  useEffect(() => {
    toastErrorMessage(null);
  }, [isOpen, toastErrorMessage]);

  return (
    // @ts-ignore
    <Modal
      title={isCreate ? 'Grant School Code' : 'Approve Update'}
      actions={
        [
          <PrimaryButton key="submit" onClick={handleSubmit} disabled={loading ? true : false}>
            Confirm
          </PrimaryButton>,
          <PrimaryCardButton key="close" onClick={onClose} disabled={loading ? true : false}>
            Close
          </PrimaryCardButton>,
        ]}
      closeButton={false}
      open={isOpen}
      onClose={onClose}
      withHeader >
      {renderToasts()}

      < Grid container spacing={2} >
        <Grid item xs={12}>
          <WordBreak>
            <strong>Organization:</strong> {request?.schoolName}
          </WordBreak>
          <WordBreak>
            <strong>Requested By:</strong> {request?.creator}
          </WordBreak>
          <WordBreak>
            <strong>Initial Submit:</strong> {(new Date(request?.createDate || 0)).toLocaleString()}
          </WordBreak>
        </Grid>;
        {
          request?.requestType === 'create' &&
          <>
            <Grid item xs={12}>
              <Input
                floating={false}
                label="School Code"
                required
                placeholder="School Code"
                maxLength={6}
                onChange={handleChangeSchoolCode}
                value={schoolCode}
                validation={!schoolCode && errorMessage ? 'error' : undefined}
                validationMsg={errorMessage ?? undefined}
              />
            </Grid>
            <Grid item xs={12}>
              {/* @ts-ignore */}
              <RadioButtonGroup
                name={'authorization'}
                required
                onChange={handleChangeAiLevel}
                value={aiLevel}
                validation={!aiLevel && errorMessage ? 'error' : undefined}
                validationMsg={errorMessage ?? undefined}
                vertical
                vSpacing="16">
                {authorizationLevelOptions.map((option) => (
                  <RadioButton key={option.value} label={option.label} value={option.value} />
                ))}
              </RadioButtonGroup>
            </Grid>
            <Grid item xs={12}>
              <div style={{ paddingRight: '8px' }}>Reviewer Comments:</div>
              <TextArea onChange={handleChangeStatusComments} value={statusComments} maxLength={5000} />
            </Grid>
          </>
        }
      </Grid >

      {loading &&
        <div style={{ marginTop: '20px' }}>
          <Grid item xs={12}><CircularProgress /> <div style={{ display: 'inline-block', fontSize: '20px' }}>{loading}</div></Grid>
        </div>
      }
    </Modal >
  );
};

export default ApprovedModal;
